import { ref, reactive, computed, onMounted } from 'vue';
import { config } from '@/config.js';
import basesService from '@/services/admin/bases/basesService';
import { useToast } from '@/composables/common/useToast';

/**
 * Composable para la vista de editar deportista (back.bases.update).
 *
 * @param {{ lista_id: number|string, base_id: number|string }} props - route params
 * @param {{ onSaved: () => void }} options - callbacks
 */
export function useEditarDeportista(props, options = {}) {
    const toast = useToast();

    const loading = ref(true);
    const submitting = ref(false);
    const error = ref(null);
    const equipos = ref([]);

    /** Formulario reactivo con los datos del deportista */
    const form = reactive({
        nombre: '',
        apellidos: '',
        dni: '',
        fecha_de_nacimiento: '',
        fecha_de_caducidad_dni: '',
        telefono: '',
        email: '',
        pais_de_nacimiento: '',
        provincia_de_nacimiento: '',
        localidad_de_nacimiento: '',
        nacionalidad: '',
        direccion: '',
        lateralidad: '',
        equipo: null,
        club_de_procedencia: '',
        observaciones: '',
        // Responsables
        nombre_responsable: '',
        apellidos_responsable: '',
        dni_responsable: '',
        email_responsable: '',
        telefono_responsable: '',
        nombre_responsable_2: '',
        apellidos_responsable_2: '',
        dni_responsable_2: '',
        email_responsable_2: '',
        telefono_responsable_2: '',
        // Foto
        foto_perfil: '',
        foto_url: '',
    });

    /** URL para subir foto */
    const fotoUploadUrl = computed(() =>
        `${config.API_BASE}/f/guardar-foto-deportista?id=${props.base_id}`,
    );

    const lateralidadOptions = [
        { id: 'Diestro', name: 'Diestro' },
        { id: 'Zurdo', name: 'Zurdo' },
        { id: 'Ambidiestro', name: 'Ambidiestro' },
    ];

    /**
     * Carga los datos del deportista.
     */
    async function fetchBase() {
        loading.value = true;
        error.value = null;

        try {
            const result = await basesService.getBase({
                lista_id: props.lista_id,
                base_id: props.base_id,
            });

            const data = result.data ?? result;

            // Mapea al formulario
            form.nombre = data.nombre || '';
            form.apellidos = data.apellidos || '';
            form.dni = data.dni || '';
            form.fecha_de_nacimiento = data.fecha_de_nacimiento || '';
            form.fecha_de_caducidad_dni = data.fecha_de_caducidad_dni || '';
            form.telefono = data.telefono || '';
            form.email = data.email || '';
            form.pais_de_nacimiento = data.pais_de_nacimiento || '';
            form.provincia_de_nacimiento = data.provincia_de_nacimiento || '';
            form.localidad_de_nacimiento = data.localidad_de_nacimiento || '';
            form.nacionalidad = data.nacionalidad || '';
            form.direccion = data.direccion || '';
            form.lateralidad = data.lateralidad || '';
            form.equipo = data.equipo?.id ?? data.equipo ?? null;
            form.club_de_procedencia = data.club_de_procedencia || '';
            form.observaciones = data.observaciones || '';
            form.nombre_responsable = data.nombre_responsable || '';
            form.apellidos_responsable = data.apellidos_responsable || '';
            form.dni_responsable = data.dni_responsable || '';
            form.email_responsable = data.email_responsable || '';
            form.telefono_responsable = data.telefono_responsable || '';
            form.nombre_responsable_2 = data.nombre_responsable_2 || '';
            form.apellidos_responsable_2 = data.apellidos_responsable_2 || '';
            form.dni_responsable_2 = data.dni_responsable_2 || '';
            form.email_responsable_2 = data.email_responsable_2 || '';
            form.telefono_responsable_2 = data.telefono_responsable_2 || '';
            form.foto_perfil = data.foto_perfil || '';
            form.foto_url = data.foto_url || '';
        } catch (err) {
            console.error('Error al cargar deportista:', err);
            error.value = 'Error al cargar los datos del deportista';
            toast.error('Error al cargar los datos del deportista');
        } finally {
            loading.value = false;
        }
    }

    /**
     * Carga la lista de equipos.
     */
    async function fetchEquipos() {
        try {
            const items = await basesService.getEquipos({
                lista_id: props.lista_id,
            });
            equipos.value = items;
        } catch (err) {
            console.error('Error al cargar equipos:', err);
        }
    }

    /**
     * Envía el formulario.
     */
    async function submit() {
        if (submitting.value) return;
        submitting.value = true;

        try {
            const payload = {
                ...form,
                base_id: props.base_id,
                lista_id: props.lista_id,
                equipo_id: form.equipo,
                filters: {},
            };

            delete payload.foto_url;
            delete payload.foto_perfil;
            delete payload.equipo;

            await basesService.updateBase(payload);

            toast.success('Deportista actualizado correctamente');

            if (options.onSaved) {
                options.onSaved();
            }
        } catch (err) {
            const msg = err.response?.data?.message || 'Error al actualizar el deportista';
            toast.error(msg);
            console.error('Error al actualizar deportista:', err);
        } finally {
            submitting.value = false;
        }
    }

    onMounted(async() => {
        await Promise.all([fetchBase(), fetchEquipos()]);
    });

    return {
        form,
        loading,
        submitting,
        error,
        equipos,
        lateralidadOptions,
        fotoUploadUrl,
        fetchBase,
        submit,
    };
}
